import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ImageOverlay,
  MapContainer,
  Marker,
  Polyline,
  useMapEvents
} from 'react-leaflet'
import { useParams } from 'react-router-dom'
import { notification } from 'antd'
import useMutation from 'hooks/useMutation'
import { CRS, LatLngTuple } from 'leaflet'
import { Building } from 'models/Building'
import {
  numberIcon,
  smallNumberIcon
} from 'pages/building/floor/area/CustomMarker'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
  buildingModeState,
  floorDetailState,
  listLinkState,
  listPointState,
  selectedPoint
} from 'store/buildingStore'
import { currentPovState } from 'store/krpanoStore'
import useSWR, { KeyedMutator } from 'swr'
import { Point } from 'types/building'
import Utils from 'utils'
import CommonFunction from 'utils/commonFunction'
import Krpano from 'utils/krpano'

import Spinner from 'components/atoms/Spinner'
import CloseIcon from 'components/icons/CloseIcon'

type Props = {
  imagePath: string | undefined
  isValidating: boolean
  mutate: KeyedMutator<any>
  isUnregisteredMap: boolean
}

export default function MenuMiniMap({
  imagePath,
  isValidating,
  mutate,
  isUnregisteredMap
}: Props) {
  const { t } = useTranslation()
  const mapContainerRef = useRef<any>(null)
  const mapContainerPreviewRef = useRef<any>(null)

  const [imgBounds, setImgBounds] = useState<LatLngTuple>([0, 0])
  const [previewImgBounds, setPreviewImgBounds] = useState<LatLngTuple>([0, 0])
  const [openPreview, setOpenPreview] = useState(false)

  const points = useRecoilValue(listPointState)
  const links = useRecoilValue(listLinkState)
  const [currentSelectedPoint, setCurrentSelectedPoint] =
    useRecoilState(selectedPoint)
  const [currentPov, setCurrentPov] = useRecoilState(currentPovState)
  const [floorDetail, setFloorDetail] = useRecoilState(floorDetailState)
  const [buildingMode, setBuildingMode] = useRecoilState(buildingModeState)

  const { trigger: downloadMap } = useMutation(`v1/buildings/files/download`)
  const { id: buildingId } = useParams()
  // handle get  building details information
  const { data: buildingInfo } = useSWR<Building>({
    url: `v1/buildings/${buildingId}`
  })

  const handleDownloadMap = async () => {
    const s3Key = CommonFunction.extractS3KeyFromUrl(
      floorDetail?.mapFile?.s3Path || ''
    )
    if (!buildingInfo || !s3Key) return

    const response = await downloadMap({
      data: {
        s3_path: s3Key,
        building_id: buildingInfo.id
      },
      headers: {
        Accept: 'image/jpeg',
        'Content-Type': 'application/json'
      },
      responseType: 'blob',
      method: 'post'
    })
    if (!response) return

    const blobData = response instanceof Blob ? response : response?.data
    // Check if we have valid blob data
    if (!(blobData instanceof Blob) || blobData.size === 0) {
      throw new Error('Invalid image data received')
    }
    // Create a new blob with the correct mime type
    const blob = new Blob([blobData], { type: 'image/jpeg' })

    if (blob.size === 0) {
      throw new Error('Generated blob is empty')
    }
    // Create and trigger download
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.style.display = 'none'
    link.href = url
    link.download = `${buildingInfo.id}_${floorDetail?.id}.png` || 'map.png'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const markerEventHandler = (point: Point) => {
    return {
      click() {
        Krpano.LoadChecking(() => {
          if (buildingMode.previewMode && !point.image360) {
            notification.error({
              key: 'redirect',
              message: '画像が設定されていません'
            })
            return
          }
          if (buildingMode.previewMode && buildingMode.selectedMode) {
            setBuildingMode((prev) => ({ ...prev, selectedMode: '' }))
          }
          setCurrentSelectedPoint(point.id)
          currentPov && setCurrentPov(null)
          setOpenPreview(false)
        })
      }
    }
  }

  function MapEventHandler() {
    useMapEvents({
      click() {
        setOpenPreview(true)
      }
    })
    return null
  }

  const initMap = (
    mapContainerRef: any,
    setImgBounds: any,
    img?: any,
    isUnregisteredMap?: boolean
  ) => {
    const height = isUnregisteredMap ? window.innerHeight : img?.naturalHeight
    const width = isUnregisteredMap ? window.innerWidth : img?.naturalWidth
    setImgBounds([height, width])
    // init bounds of map
    const initBound = [
      [0, 0],
      [height, width]
    ]
    // calculate and set MinZoom + MaxZoom level
    mapContainerRef.current?.setMinZoom(-999)
    mapContainerRef.current?.fitBounds(initBound, { animate: false })
    mapContainerRef.current?.setMaxBounds(initBound)
    const minZoomLevel = mapContainerRef.current?.getBoundsZoom(initBound)
    mapContainerRef.current?.setView([height / 2, width / 2], minZoomLevel, {
      animate: false
    })
    mapContainerRef.current?.setMinZoom(minZoomLevel)
    mapContainerRef.current?.setMaxZoom(minZoomLevel + 5)
  }

  const mapOnReady = (
    mapContainerRef: any,
    setImgBounds: any,
    isUnregisteredMap: boolean
  ) => {
    Utils.getMeta(imagePath)
      .then((img) => {
        initMap(mapContainerRef, setImgBounds, img, isUnregisteredMap)
      })
      .catch(() => {
        mutate().then((floor: any) => {
          setFloorDetail(floor)
          Utils.getMeta(floor?.mapFile.s3Path).then((img) => {
            initMap(mapContainerRef, setImgBounds, img, isUnregisteredMap)
          })
        })
      })
  }

  return imagePath ? (
    <>
      {isValidating ? <Spinner /> : null}
      <div className="p-[10px] h-44 border-0 border-t border-b border-solid theme-border-secondary">
        <MapContainer
          ref={mapContainerRef}
          center={[-99999, -99999]}
          zoom={0}
          zoomSnap={0.1}
          zoomDelta={0.5}
          className="area-map-container theme-bg-third"
          style={{
            outlineStyle: 'none'
          }}
          crs={CRS.Simple}
          zoomControl={false}
          attributionControl={false}
          preferCanvas
          whenReady={() =>
            mapOnReady(mapContainerRef, setImgBounds, isUnregisteredMap)
          }
        >
          <MapEventHandler />

          {points?.map((point) => (
            <Marker
              key={`marker ${point.id}`}
              draggable={false}
              zIndexOffset={
                point.id === currentSelectedPoint ? 450 : 400 + point.order
              }
              // leaflet position is latlng with y=lat x=lng
              position={[point.y, point.x]}
              icon={smallNumberIcon(point, currentSelectedPoint)}
            />
          ))}

          {links?.map((link) => (
            <Polyline
              key={link.id}
              // leaflet position is latlng with y=lat x=lng
              positions={[
                [link.point1.y, link.point1.x],
                [link.point2.y, link.point2.x]
              ]}
              color="#000000"
              weight={2}
            />
          ))}
          <ImageOverlay url={imagePath} bounds={[[0, 0], imgBounds]} />
        </MapContainer>
      </div>

      {openPreview && (
        <div className="absolute top-0 left-0 w-screen h-full flex bg-[#00000099] z-[750]">
          <div className="m-auto flex w-[1006px] h-[580px] bg-white relative rounded-[20px]">
            <div
              className="absolute top-5 right-5 cursor-pointer"
              onClick={() => setOpenPreview(false)}
            >
              <CloseIcon />
            </div>

            <div className="w-[700px] h-[490px] m-auto">
              <MapContainer
                ref={mapContainerPreviewRef}
                center={[-99999, -99999]}
                zoom={0}
                zoomSnap={0.1}
                zoomDelta={0.5}
                className="area-map-container"
                crs={CRS.Simple}
                zoomControl={false}
                attributionControl={false}
                preferCanvas
                whenReady={() => {
                  mapOnReady(
                    mapContainerPreviewRef,
                    setPreviewImgBounds,
                    isUnregisteredMap
                  )
                }}
              >
                {points?.map((point) => (
                  <Marker
                    key={`preview-marker-${point.id}`}
                    draggable={false}
                    bubblingMouseEvents={false}
                    eventHandlers={markerEventHandler(point)}
                    zIndexOffset={
                      point.id === currentSelectedPoint
                        ? 450
                        : 400 + point.order
                    }
                    // leaflet position is latlng with y=lat x=lng
                    position={[point.y, point.x]}
                    icon={numberIcon(
                      point,
                      false,
                      point.id === currentSelectedPoint
                    )}
                  />
                ))}

                {links?.map((link) => (
                  <Polyline
                    key={`preview-link-${link.id}`}
                    // leaflet position is latlng with y=lat x=lng
                    positions={[
                      [link.point1.y, link.point1.x],
                      [link.point2.y, link.point2.x]
                    ]}
                    color="#000000"
                    weight={2}
                  />
                ))}

                <ImageOverlay
                  url={imagePath}
                  bounds={[[0, 0], previewImgBounds]}
                />
              </MapContainer>
            </div>

            <div
              className={`absolute bottom-12 right-5 select-none ${
                isUnregisteredMap ? 'cursor-not-allowed' : ''
              }`}
            >
              <div
                className={`related-point-button text-xs font-bold h-[25px] w-[116px] border border-black rounded-[10px] flex justify-center items-center transition duration-150 bg-white hover:bg-gray-100 hover:shadow active:bg-gray-200 active:scale-95 active:shadow-inner select-none
                ${
                  isUnregisteredMap
                    ? 'opacity-50 pointer-events-none bg-gray-100 text-gray-400'
                    : 'cursor-pointer'
                }
              `}
                onClick={isUnregisteredMap ? undefined : handleDownloadMap}
              >
                {t('building.minimap.download')}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  ) : (
    <div className="w-full h-full flex items-center justify-center bg-gray-100">
      <div className="text-gray-500 text-center p-4">
        <div className="text-lg font-medium mb-2">地図が利用できません</div>
      </div>
    </div>
  )
}
